/**
 * Radio Component
 * 
 * Radio buttons for single selection from multiple options.
 * Built on MUI Radio with HAAT styling.
 */

import React from 'react';
import MuiRadio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { motion } from 'framer-motion';

// =============================================================================
// TYPES
// =============================================================================

export interface RadioOption {
  /** Option value */
  value: string | number;
  /** Option label */
  label: string;
  /** Optional description */
  description?: string;
  /** Disabled state for this option */
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Group label */
  label?: string;
  /** Radio options */
  options: RadioOption[];
  /** Currently selected value */
  value: string | number | null;
  /** Change handler */
  onChange: (value: string) => void;
  /** Disabled state for entire group */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Layout direction */
  direction?: 'row' | 'column';
  /** Size variant */
  size?: 'small' | 'medium';
  /** Required field */
  required?: boolean;
  /** Name attribute for form */
  name?: string;
  /** Additional class name */
  className?: string;
}

export interface RadioProps {
  /** Radio label */
  label: string;
  /** Optional description */
  description?: string;
  /** Radio value */
  value: string | number;
  /** Checked state */
  checked: boolean;
  /** Change handler */
  onChange: (value: string) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Name attribute */
  name?: string;
}

// =============================================================================
// SINGLE RADIO COMPONENT
// =============================================================================

export const Radio: React.FC<RadioProps> = ({
  label,
  description,
  value,
  checked,
  onChange,
  disabled = false,
  size = 'medium',
  name,
}) => {
  return (
    <FormControlLabel
      control={
        <MuiRadio
          checked={checked}
          onChange={(e) => onChange(e.target.value)}
          value={value}
          disabled={disabled}
          size={size}
          name={name}
          sx={{
            color: '#9ca3af',
            '&.Mui-checked': {
              color: 'rgb(var(--color-primary-500))',
            },
            '&:hover': {
              backgroundColor: 'rgba(var(--color-primary-500), 0.04)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        />
      }
      label={
        <div className="flex flex-col">
          <span className={`
            text-sm font-medium
            ${disabled ? 'text-gray-400' : 'text-gray-700'}
          `}>
            {label}
          </span>
          {description && (
            <span className={`
              text-xs mt-0.5
              ${disabled ? 'text-gray-300' : 'text-gray-500'}
            `}>
              {description}
            </span>
          )}
        </div>
      }
      disabled={disabled}
      sx={{
        alignItems: description ? 'flex-start' : 'center',
        marginLeft: '-9px',
        '& .MuiFormControlLabel-label': {
          marginTop: description ? '2px' : 0,
        },
      }}
    />
  );
};

// =============================================================================
// RADIO GROUP COMPONENT
// =============================================================================

export const RadioGroupComponent: React.FC<RadioGroupProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  error = false,
  errorMessage,
  direction = 'column',
  size = 'medium',
  required = false,
  name,
  className = '',
}) => {
  const hasError = error || !!errorMessage;

  return (
    <FormControl
      component="fieldset"
      error={hasError}
      disabled={disabled}
      className={className}
    >
      {label && (
        <FormLabel
          component="legend"
          required={required}
          sx={{
            color: hasError ? '#c00035' : '#374151',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: '8px',
            '&.Mui-focused': {
              color: hasError ? '#c00035' : '#374151',
            },
          }}
        >
          {label}
        </FormLabel>
      )}
      <RadioGroup
        name={name}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        row={direction === 'row'}
        sx={{
          gap: direction === 'row' ? '16px' : '4px',
        }}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <MuiRadio
                size={size}
                sx={{
                  color: hasError ? '#c00035' : '#9ca3af',
                  '&.Mui-checked': {
                    color: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
                  },
                  '&:hover': {
                    backgroundColor: hasError
                      ? 'rgba(220, 38, 38, 0.04)'
                      : 'rgba(var(--color-primary-500), 0.04)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              />
            }
            value={option.value}
            disabled={option.disabled || disabled}
            label={
              <div className="flex flex-col">
                <span className={`
                  text-sm font-medium
                  ${option.disabled || disabled ? 'text-gray-400' : hasError ? 'text-[#c00035]' : 'text-gray-700'}
                `}>
                  {option.label}
                </span>
                {option.description && (
                  <span className={`
                    text-xs mt-0.5
                    ${option.disabled || disabled ? 'text-gray-300' : 'text-gray-500'}
                  `}>
                    {option.description}
                  </span>
                )}
              </div>
            }
            sx={{
              alignItems: option.description ? 'flex-start' : 'center',
              marginLeft: '-9px',
              '& .MuiFormControlLabel-label': {
                marginTop: option.description ? '2px' : 0,
              },
            }}
          />
        ))}
      </RadioGroup>
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <FormHelperText sx={{ marginLeft: '2px', marginTop: '4px' }}>
            {errorMessage}
          </FormHelperText>
        </motion.div>
      )}
    </FormControl>
  );
};

export { RadioGroupComponent as RadioGroup };

export default Radio;

